import type { State } from '../State';
import {
  PetAttribute,
  PetEvolutionStage,
  PetSpeciesID,
  PetState,
} from '../pet';
import * as Pet from '../pet';
import * as Fate from '../fate';
import createAsyncGetters, {
  AsyncGetterAPI,
} from '../config/asyncGetters/createAsyncGetters';
import { getSharedStateAsyncGettersAPI } from '../config/asyncGetters/createSharedStateAsyncGetters';
import { getLocale } from '../getters/user';
import { AvatarFrameId } from '../profile/frames';
import {
  getPlayerProfileFromLocale,
  getPlayerProfile,
  getPlayerProfileAvatarFrame,
} from '../profile/profile.getters';
import { getPuzzleWinsLast7Days } from '../puzzleWinCount/puzzleWinCount.getters';
import features, { Features } from '../ruleset/features';
import { Locale } from '../ruleset/locale';
import { getBuruFriends } from './friends.getters';
import { clamp } from '../utils';
import {
  getUnfriendedFriends,
  getUsersWithPendingRequest,
} from './friends.getters';
import {
  ExternalFriend,
  PlayerEntryData,
  PlayerProfile,
  friendListLimit,
} from './friends.ruleset';
import { getVisitedCount, getVisitsCount } from '../visit/visit.getters';
import { isOnlineForVisit } from '../visit/visit.presence';

export const friendsAsyncGetters = createAsyncGetters({
  /**
   * Get friends supported feature
   */
  getFriendsFeatures: async (
    ids: string[],
    api,
  ): Promise<{ [id: string]: Features }> => {
    const state = await api.fetchStates(ids);
    const entries = Object.entries(state).map(([id, player]) => [
      id,
      features(player.state),
    ]);

    return Object.fromEntries(entries);
  },

  /**
   * Get players profiles
   */
  fetchPlayersProfiles: async (
    opts: { ids: string[]; locale: Locale },
    api,
  ): Promise<ExternalFriend[]> => {
    // At most `friendListLimit` profiles are returned, so querying more IDs than
    // that only grows the request without returning more profiles. Callers should
    // pass no more than `friendListLimit` IDs, in priority order; this guards the
    // query regardless.
    const ids = opts.ids.slice(0, friendListLimit);

    if (ids.length === 0) return [];

    /**
     * For local development, we can use the offline mode to simulate
     */
    if (process.env.IS_LOCAL_DEVELOPMENT) {
      const state = await api.fetchStates(ids);
      return Object.entries(state).map(([id, { state }]) => {
        const profile = getPlayerProfile(
          state,
          state.publicProfile?.name,
          state.publicProfile?.photo,
          state.profile?.name,
          state.profile?.photo,
        );

        return {
          id,
          isPlayer: true,
          profilePicture: profile.photo,
          frame: getPlayerProfileAvatarFrame(state),
          name: profile.name,
        };
      });
    }

    /**
     * Production mode fetches profiles from the server
     */
    const search = await api.searchPlayers({
      where: {
        id: {
          isOneOf: ids,
        },
      },
      fields: [
        'profileName',
        'profilePhoto',
        'hasPublicProfile',
        'publicProfileName',
        'publicProfilePhoto',
        'profileAvatarFrame',
      ],
      limit: clamp(ids.length, 1, friendListLimit),
    });

    return search.results.map((data) => {
      const profile = getPlayerProfileFromLocale(
        opts.locale,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );

      return {
        id: data.id,
        isPlayer: true,
        profilePicture: profile.photo,
        frame: data.profileAvatarFrame as AvatarFrameId,
        name: profile.name,
      };
    });
  },

  /**
   * Search non-friend players by public profile name
   */
  searchNonFriendPlayers: async (
    opts: { searchStr: string },
    api,
  ): Promise<PlayerProfile[]> => {
    const ownState = await api.getOwnState();
    const friends = getBuruFriends(ownState);
    const pendingRequestUserIds = getUsersWithPendingRequest(ownState);
    const unfriendedFriends = getUnfriendedFriends(ownState);

    const search = await api.searchPlayers({
      where: {
        hasPublicProfile: {
          is: true,
        },
        id: {
          isNotOneOf: [
            ownState.id,
            ...friends,
            ...pendingRequestUserIds,
            ...unfriendedFriends,
          ],
        },
        publicProfileNameSearchable: {
          matchesAnyOf: [opts.searchStr],
        },
      },
      fields: [
        'hasPublicProfile',
        'publicProfileName',
        'publicProfilePhoto',
        'profileAvatarFrame',
        'petSpecies',
      ],
      limit: friendListLimit,
    });

    return search.results.map((data) => {
      const profile = getPlayerProfile(
        ownState,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );

      return {
        id: data.id,
        name: profile.name,
        profilePicture: profile.photo,
        frame: data.profileAvatarFrame as AvatarFrameId,
        species: data.petSpecies || 'ball',
      };
    });
  },

  /**
   * Search non-friend player entries by public profile name
   */
  searchNonFriendPlayerEntries: async (
    opts: { searchStr: string },
    api,
  ): Promise<PlayerEntryData[]> => {
    const ownState = await api.getOwnState();
    const locale = getLocale(ownState);
    const friends = getBuruFriends(ownState);
    const pendingRequestUserIds = getUsersWithPendingRequest(ownState);
    const unfriendedFriends = getUnfriendedFriends(ownState);

    const search = await api.searchPlayers({
      where: {
        hasPublicProfile: {
          is: true,
        },
        id: {
          isNotOneOf: [
            ownState.id,
            ...friends,
            ...pendingRequestUserIds,
            ...unfriendedFriends,
          ],
        },
        publicProfileNameSearchable: {
          matchesAnyOf: [opts.searchStr],
        },
      },
      fields: [
        'updatedAt',
        'profileName',
        'profilePhoto',
        'publicProfileName',
        'publicProfilePhoto',
        'profileAvatarFrame',
        'match2Level',
        'petSpecies',
        'petName',
        'petState',
        'presence',
        'petIsInDaycare',
        'currentTeamId',
      ],
      limit: friendListLimit,
    });

    const players = search.results;
    const teamIds = players.flatMap((p) =>
      p.currentTeamId ? [p.currentTeamId] : [],
    );
    const teamIdsSet = new Set(teamIds);
    const teamProfiles = await getTeamNamesAndIcons(api, [...teamIdsSet]);

    return players.map((data) => {
      const profile = getPlayerProfileFromLocale(
        locale,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );
      const teamProfile = data.currentTeamId
        ? teamProfiles[data.currentTeamId]
        : undefined;

      return {
        updatedAt: data.updatedAt,
        id: data.id,
        name: profile.name,
        photo: profile.photo,
        level: data.match2Level,
        avatarFrame: data.profileAvatarFrame as AvatarFrameId,
        petSpecies: data.petSpecies || 'ball',
        petName: data.petName,
        petState: data.petState,
        isFriend: false,
        isOnline: isOnlineForVisit(data.presence),
        canVisit: !isOnlineForVisit(data.presence) && !data.petIsInDaycare,
        teamName: teamProfile?.name,
        teamIcon: teamProfile?.icon,
      };
    });
  },

  /**
   * Fetch the list of friends with profile info
   */
  fetchFriendList: async (opts: {}, api): Promise<PlayerEntryData[]> => {
    const state = await api.getOwnState();
    const locale = getLocale(state);
    const friendsIds = getBuruFriends(state);

    const search = await api.searchPlayers({
      where: {
        id: {
          isOneOf: friendsIds,
        },
      },
      fields: [
        'updatedAt',
        'profileName',
        'profilePhoto',
        'publicProfileName',
        'publicProfilePhoto',
        'profileAvatarFrame',
        'match2Level',
        'petSpecies',
        'petName',
        'petState',
        'presence',
        'petIsInDaycare',
        'currentTeamId',
      ],
      sort: [
        {
          field: 'match2Level',
          order: 'desc',
        },
      ],
      limit: clamp(friendsIds.length, 0, friendListLimit),
    });

    const players = search.results;
    const teamIds = players.flatMap((p) =>
      p.currentTeamId ? [p.currentTeamId] : [],
    );
    const teamIdsSet = new Set(teamIds);
    const teamProfiles = await getTeamNamesAndIcons(api, [...teamIdsSet]);

    return players.map((data) => {
      const profile = getPlayerProfileFromLocale(
        locale,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );
      const teamProfile = data.currentTeamId
        ? teamProfiles[data.currentTeamId]
        : undefined;

      return {
        updatedAt: data.updatedAt,
        id: data.id,
        name: profile.name,
        photo: profile.photo,
        level: data.match2Level,
        avatarFrame: data.profileAvatarFrame as AvatarFrameId,
        petSpecies: data.petSpecies || 'ball',
        petName: data.petName,
        petState: data.petState,
        isFriend: true,
        isOnline: isOnlineForVisit(data.presence),
        canVisit: !isOnlineForVisit(data.presence) && !data.petIsInDaycare,
        teamName: teamProfile?.name,
        teamIcon: teamProfile?.icon,
      };
    });
  },

  /**
   * Fetch the list of players suggested as friends.
   *
   * Prioritizing player with number of wins in the last 7 days
   * within +/- 10% of the user's wins (+5% bias).
   *
   * And match2 level scored with Gauss decay (±50 levels).
   *
   * Prioritizes users with 0 friends first, then those with < 5 friends.
   */
  fetchFriendSuggestions: async (_: {}, api): Promise<PlayerEntryData[]> => {
    const count = 5;
    const state = await api.getOwnState();
    const locale = getLocale(state);

    const search = await searchFriendsToSuggest({
      api,
      count,
    });

    const players = search.results;
    const teamIds = players.flatMap((p) =>
      p.currentTeamId ? [p.currentTeamId] : [],
    );
    const teamIdsSet = new Set(teamIds);
    const teamProfiles = await getTeamNamesAndIcons(api, [...teamIdsSet]);

    return players.map((data) => {
      const profile = getPlayerProfileFromLocale(
        locale,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );
      const teamProfile = data.currentTeamId
        ? teamProfiles[data.currentTeamId]
        : undefined;

      return {
        updatedAt: data.updatedAt,
        id: data.id,
        name: profile.name,
        photo: profile.photo,
        level: data.match2Level,
        avatarFrame: data.profileAvatarFrame as AvatarFrameId,
        petName: data.petName,
        petSpecies: data.petSpecies || 'ball',
        petState: data.petState,
        isFriend: false,
        isOnline: isOnlineForVisit(data.presence),
        canVisit: !isOnlineForVisit(data.presence) && !data.petIsInDaycare,
        teamName: teamProfile?.name,
        teamIcon: teamProfile?.icon,
      };
    });
  },

  /**
   * Fetch a user's full profile details for display in the user profile popup.
   * Unlike getPetVisitDetails, this does not require friendship and returns
   * profile data for any player. When no public profile is set, it uses
   * locale-based default values which can be overridden by the caller.
   */
  fetchFullUserProfile: async (
    playerId: string,
    api,
  ): Promise<UserProfileDetails | null> => {
    const statesMap = await api.fetchStates([playerId]);
    const playerState = statesMap[playerId];
    if (!playerState) return null;

    const state = playerState.state as State;
    const ownState = await api.getOwnState();
    const now = api.date.now();
    const profile = getPlayerProfile(
      state,
      state.publicProfile?.name,
      state.publicProfile?.photo,
    );

    return {
      id: state.id,
      hasPublicProfile: !!state.publicProfile,
      name: profile.name,
      photo: profile.photo,
      frame: getPlayerProfileAvatarFrame(state),
      lastLogin: state.lastSessionAt,
      visits: getVisitsCount(ownState, state.id),
      visited: getVisitedCount(ownState, state.id),
      canAcceptFriendRequest: true,
      ...getPetDetails(state, now),
    };
  },
});

export type UserProfileDetails = {
  id: string;
  hasPublicProfile?: boolean;
  name: string;
  photo: string;
  frame: AvatarFrameId;
  lastLogin: number;
  visits: number;
  visited: number;
  canAcceptFriendRequest: boolean;
  level: number;
  age: number;
  currentEvolution: {
    species: PetSpeciesID;
    stage: PetEvolutionStage;
  };
  state: PetState;
  attributes: Record<PetAttribute, number> & { name: string; stress: number };
};

type FriendSuggestionsSearchOpts = {
  api: AsyncGetterAPI;
  count: number;
};

async function searchFriendsToSuggest(opts: FriendSuggestionsSearchOpts) {
  const { api, count } = opts;
  const state = await api.getOwnState();
  const friendsIds = getBuruFriends(state);
  const pendingRequestUserIds = getUsersWithPendingRequest(state);
  const unfriendedIds = getUnfriendedFriends(state);

  // User's number of wins in the last 7 days
  const userWinsLast7Days = getPuzzleWinsLast7Days(
    state,
    api.date.now(),
  ).reduce((acc, val) => acc + val, 0);

  // Minimum should be 1 for the % calculation. Prioritize players with 5% more wins than current user.
  const baseUserWins = Math.max(1, userWinsLast7Days) * 1.05;

  return api.searchPlayers({
    scoring: {
      functions: {
        buruFriendsCount: {
          // Prioritize users with 0 friends, then those with < 5 friends
          origin: 0,
          function: 'gauss',
          scale: 5,
          decay: 0.5,
        },
        puzzleWinsLast7Days: {
          // The base number of wins is used as the origin, cannot be 0
          origin: baseUserWins,
          // Gauss decreases smoothly as we move away from the origin
          function: 'gauss',
          // The scale is ±10% of the base number of wins, minimum 1
          scale: Math.ceil(baseUserWins * 0.1),
          // The decay is 50% of the scale
          decay: 0.5,
        },
        match2Level: {
          // The origin is the user's current level
          origin: state.match2.level,
          // Gauss decreases smoothly as we move away from the origin
          function: 'gauss',
          // The scale is ±50 levels of the user's current level
          scale: 50,
          // The decay is 50% of the scale
          decay: 0.5,
        },
      },
      mode: 'multiply',
    },
    where: {
      id: {
        isNotOneOf: [
          ...friendsIds,
          ...unfriendedIds,
          ...pendingRequestUserIds,
          state.id,
        ],
      },
      hasPublicProfile: {
        is: true,
      },
    },
    fields: [
      'updatedAt',
      'profileName',
      'profilePhoto',
      'publicProfileName',
      'publicProfilePhoto',
      'profileAvatarFrame',
      'petName',
      'petSpecies',
      'match2Level',
      'petState',
      'presence',
      'petIsInDaycare',
      'currentTeamId',
    ],
    limit: count,
  });
}

export async function getTeamNamesAndIcons(
  rawApi: AsyncGetterAPI,
  teamIds: string[],
): Promise<Record<string, { name: string; icon: string }>> {
  const api = getSharedStateAsyncGettersAPI(rawApi);
  const teams = await api.sharedStates.team.search({
    where: {
      id: {
        isOneOf: teamIds,
      },
    },
    limit: teamIds.length,
  });

  return teams.results.reduce(
    (acc, team) => {
      acc[team.id] = {
        name: team.profile.name,
        icon: team.profile.icon,
      };
      return acc;
    },
    {} as Record<string, { name: string; icon: string }>,
  );
}

function getPetDetails(state: State, now: number) {
  const currentEvolution = Pet.getters.getCurrentEvolution(state);
  const hasPet = !!currentEvolution?.species;

  if (!hasPet) {
    // If the player doesn't have a pet, return default values.
    // Useful for edge cases when we are fetching the profile of a player who hasn't created a pet yet.
    return {
      currentEvolution: {
        species: 'ball' as PetSpeciesID,
        stage: 'baby' as PetEvolutionStage,
      },
      state: 'normal' as PetState,
      age: 0,
      level: 1,
      attributes: {
        name: state.pet?.name || '',
        stomach: 39,
        love: 41,
        dirtiness: 0,
        urine: 0,
        toothAcidity: 0,
        stress: 0,
      },
    };
  }

  // If the player has a pet, return the actual pet details.
  return {
    currentEvolution: {
      species: currentEvolution.species,
      stage: currentEvolution.stage,
    },
    state: Fate.getters.getVisiblePetState(state, now, true),
    age: Pet.getters.getPetAge(state, now),
    level: state.level.current,
    attributes: {
      name: Pet.getters.getPetName(state),
      ...Pet.getters.getAttributeValues(state, now),
      stress: Pet.getters.getStressValue(state, now),
    },
  };
}
